// use-auth-init — one-shot hook that bridges anon-id to AuthSlice.
//
// Called once in App component. Reads the existing anonymous ID and
// populates store.currentUser. When a real account system lands,
// this hook grows to check for existing server sessions.
//
// 19BN Phase 2: after auth initializes, if the user is authenticated,
// fetch their point balance (account-slice.refreshBalance).
//
// Session 43: Account frontend stub → Phase 1: real session → Phase 2: balance.

import { useEffect } from 'react';
import { useStore } from '../store/index.js';

/**
 * Initialize auth state from anonymous ID (or future real session).
 * Call once at app root level (inside App component).
 */
export function useAuthInit(): void {
  const initializeAuth = useStore((s) => s.initializeAuth);
  const isAuthLoading = useStore((s) => s.isAuthLoading);
  const currentUser = useStore((s) => s.currentUser);
  const refreshBalance = useStore((s) => s.refreshBalance);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // When auth finishes loading and the user is authenticated, fetch balance
  // + inject remote notification service.
  useEffect(() => {
    if (!isAuthLoading && currentUser && !currentUser.isAnonymous) {
      void refreshBalance();
      // 19BN Phase 5: inject RemoteNotificationService + sync remote notifications.
      void (async () => {
        try {
          const { RemoteNotificationService } = await import('../remote-notification-service.js');
          const svc = new RemoteNotificationService(currentUser.id);
          // notification-slice's _remoteService is internal; use setState to inject.
          const state = useStore.getState() as { setRemoteService?: (s: unknown) => void; syncFromRemote?: () => void };
          if (state.setRemoteService) state.setRemoteService(svc);
          if (state.syncFromRemote) state.syncFromRemote();
        } catch {
          // non-critical — local notifications still work
        }
      })();
    }
  }, [isAuthLoading, currentUser, refreshBalance]);
}
